import React, { useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { Box, useTheme } from '@mui/material'
import { SelectedBid, PartialMaturity } from '../../shared/types'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import {
  formatCurrency,
  formatPercentage,
  formatDateTime,
  formatWamYears,
  formatPartialMaturities,
} from '../../shared/formats'

// Extended interface for table display
interface SelectedBidTableRow extends SelectedBid {
  issuerName: string
  scenarioName: string
  sotr: number | null
}

interface SelectedBidsTableProps {
  selectedBids: SelectedBidTableRow[]
  isLoading?: boolean
}

export const SelectedBidsTable: React.FC<SelectedBidsTableProps> = ({
  selectedBids,
  isLoading = false,
}) => {
  const theme = useTheme()
  // Column definitions for ag-grid
  const columnDefs: ColDef<SelectedBidTableRow>[] = useMemo(
    () => [
      {
        headerName: 'Scenario',
        field: 'scenarioName',
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: 'Issuer',
        field: 'issuerName',
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: 'Rate',
        field: 'rate',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 100,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, number>
        ) => formatPercentage(params.value),
      },
      {
        headerName: 'Treasury Rate',
        field: 'treasuryRate',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 130,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, number>
        ) => formatPercentage(params.value),
      },
      {
        headerName: 'SOTR',
        field: 'sotr',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 100,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, number>
        ) => formatPercentage(params.value),
      },
      {
        headerName: 'Amount',
        field: 'amount',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 150,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, number>
        ) => formatCurrency(params.value),
      },
      {
        headerName: 'WAM (Years)',
        field: 'wamYears',
        sortable: true,
        filter: 'agNumberColumnFilter',
        width: 120,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, number>
        ) => formatWamYears(params.value),
      },
      {
        headerName: 'Partial Maturities',
        field: 'partialMaturities',
        sortable: false,
        filter: false,
        width: 250,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, PartialMaturity[]>
        ) => formatPartialMaturities(params.value),
      },
      {
        headerName: 'Selected Date',
        field: 'createdAt',
        sortable: true,
        filter: 'agDateColumnFilter',
        width: 150,
        valueFormatter: (
          params: ValueFormatterParams<SelectedBidTableRow, string>
        ) => formatDateTime(params.value),
      },
    ],
    []
  )

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  )

  return (
    <Box
      id="selected-bids-table"
      className="ag-theme-quartz-auto-dark ag-theme-cwan compact-grid"
      sx={{
        minHeight: '200px',
        maxHeight: '600px',
        width: '100%',
        border: `1px solid ${theme.palette.grey[200]}`,
        overflow: 'auto',
      }}
    >
      <AgGridReact<SelectedBidTableRow>
        theme="legacy" // Use legacy theming for v32 CSS compatibility
        rowData={selectedBids}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        loading={isLoading}
        animateRows={true}
        suppressCellFocus={true}
        rowHeight={40} // match row height of bids table
        getRowId={(params) =>
          `${params.data.scenarioName}-${params.data.issuerTenantId}`
        } // Unique row ID
        domLayout="autoHeight"
      />
    </Box>
  )
}

export type { SelectedBidTableRow }
